import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Chip, useTheme } from '@mui/material';
import { Receipt, Schedule, CheckCircle, Warning } from '@mui/icons-material';
import styled from '@emotion/styled';
import BaseWidget from './BaseWidget';
import { useFetchQueue } from '../hooks/useFetchQueue';
import { getInvoicesByBillingEntities as requestGetInvoicesByBillingEntities } from '../redux/sagas/requests/invoices';
import { statusColor } from '../utils/statusColors';
import { formatDayShort } from '../utils/dateUtils';

const InvoiceList = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
});

const InvoiceCard = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isOpen',
})(({ theme, isOpen }) => ({
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: isOpen ? theme.palette.warning.light + '20' : 'transparent',
    '&:last-child': {
        marginBottom: 0,
    },
}));

const InvoiceHeader = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
});

const InvoiceNumber = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.size.md,
    flex: 1,
    cursor: 'pointer',
    '&:hover': {
        color: theme.palette.primary.dark,
    },
}));

const StatusChip = styled(Chip)(({ theme, statuscolor }) => ({
    height: 22,
    fontSize: theme.typography.size.xs,
    backgroundColor: statuscolor,
    color: '#fff',
}));

const InvoiceDetails = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
}));

const DetailRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: theme.typography.size.sm,
}));

const DetailLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
}));

const DetailValue = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
}));

const AmountValue = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.md,
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.text.primary,
}));

const EmptyState = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '150px',
    color: theme.palette.text.secondary,
}));

const EmptyStateIcon = styled(Receipt)(({ theme }) => ({
    fontSize: 40,
    marginBottom: theme.spacing(1),
    opacity: 0.5,
}));

const InlineDateIcon = styled(Schedule)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    marginRight: theme.spacing(0.5),
    verticalAlign: 'middle',
}));

const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
    }).format(amount);
};

const formatDate = (date) => formatDayShort(date) || 'N/A';

const InvoicesWidget = ({ widgetId, config = {}, onRemove }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const { data: invoices, isLoading, error } = useFetchQueue(widgetId, {
        apiCall: () => requestGetInvoicesByBillingEntities(),
        cacheKey: 'invoices-by-billing-entities',
    });

    const hasData = (invoices || []).length > 0;

    return (
        <BaseWidget
            title="Invoices"
            widgetId={widgetId}
            onRemove={onRemove}
            isLoading={isLoading}
            error={error}
        >
            {!hasData ? (
                <EmptyState>
                    <EmptyStateIcon />
                    <Typography variant="body2">
                        No invoices found
                    </Typography>
                </EmptyState>
            ) : (
                <InvoiceList>
                    {(invoices || []).map((invoice) => {
                        const isOpen = invoice.status === 'Open';
                        return (
                            <InvoiceCard key={invoice._id} isOpen={isOpen}>
                                <InvoiceHeader>
                                    <InvoiceNumber
                                        onClick={() => navigate(`/client/${invoice.client?.slug}/invoices/${invoice._id}`)}
                                    >
                                        {invoice.invoiceNumber}
                                    </InvoiceNumber>
                                    <StatusChip
                                        label={invoice.status}
                                        size="small"
                                        statuscolor={statusColor(theme, 'invoice', invoice.status)}
                                        icon={isOpen ? <Warning fontSize="inherit" /> : <CheckCircle fontSize="inherit" />}
                                    />
                                </InvoiceHeader>

                                <InvoiceDetails>
                                    <DetailRow>
                                        <DetailLabel>
                                            <InlineDateIcon fontSize="small" />
                                            Date
                                        </DetailLabel>
                                        <DetailValue>{formatDate(invoice.invoiceDate)}</DetailValue>
                                    </DetailRow>
                                    {invoice.client && (
                                        <DetailRow>
                                            <DetailLabel>Client</DetailLabel>
                                            <DetailValue>{invoice.client.name}</DetailValue>
                                        </DetailRow>
                                    )}
                                    {invoice.account && (
                                        <DetailRow>
                                            <DetailLabel>Account</DetailLabel>
                                            <DetailValue>{invoice.account.name}</DetailValue>
                                        </DetailRow>
                                    )}
                                    <DetailRow>
                                        <DetailLabel>Amount</DetailLabel>
                                        <AmountValue>{formatCurrency(invoice.amount, invoice.currency)}</AmountValue>
                                    </DetailRow>
                                </InvoiceDetails>
                            </InvoiceCard>
                        );
                    })}
                </InvoiceList>
            )}
        </BaseWidget>
    );
};

export default InvoicesWidget;
